/**
 * VaultModule - Production NWGSV Vault Interface
 * File upload, categorization, search, download, and delete
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiCall } from '../utils/api';

interface Category {
  id: string;
  name: string;
  description?: string;
  created_by: string;
  created_at: string;
  file_count: number;
}

interface VaultFile {
  id: string;
  filename: string;
  original_name: string;
  category_id?: string;
  tags: string[];
  description?: string;
  uploaded_by: string;
  uploaded_at: string;
  file_size: number;
  mime_type: string;
}

export default function VaultModule() {
  const { user } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [files, setFiles] = useState<VaultFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<VaultFile | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showUploadForm, setShowUploadForm] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedCategory, searchQuery]);

  // Check access
  if (user && user.role !== 'SUPER_ADMIN' && user.role !== 'FAMILY') {
    return (
      <div style={styles.accessDenied}>
        <div style={styles.accessTitle}>ACCESS RESTRICTED</div>
        <div style={styles.accessMessage}>
          Vault access requires SUPER_ADMIN or FAMILY role.
        </div>
        <div style={styles.accessRole}>Your role: {user.role}</div>
      </div>
    );
  }

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load categories
      const catResponse = await apiCall('/api/vault/categories');
      if (catResponse.ok) {
        const catData = await catResponse.json();
        setCategories(catData);
      }

      // Load files
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category_id', selectedCategory);
      if (searchQuery) params.append('search', searchQuery);
      
      const filesResponse = await apiCall(`/api/vault/files?${params}`);
      if (filesResponse.ok) {
        const filesData = await filesResponse.json();
        setFiles(filesData);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load vault data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setUploading(true);
    setError('');

    try {
      const formData = new FormData(e.currentTarget);
      
      const response = await fetch('/api/vault/files/upload', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Upload failed');
      }

      setShowUploadForm(false);
      await loadData();
      (e.target as HTMLFormElement).reset();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (fileId: string, filename: string) => {
    try {
      const response = await fetch(`/api/vault/files/${fileId}/download`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (fileId: string) => {
    if (!window.confirm('Delete this file permanently?')) return;

    try {
      const response = await apiCall(`/api/vault/files/${fileId}/delete`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Delete failed');
      }

      setSelectedFile(null);
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div className="module-root" style={styles.container}>
      {/* Header */}
      <div className="module-header" style={styles.header}>
        <div>
          <h2 style={styles.title}>SOVEREIGN VAULT</h2>
          <p style={styles.subtitle}>Secure Document Storage & Management</p>
        </div>
        <button
          style={styles.uploadButton}
          onClick={() => setShowUploadForm(!showUploadForm)}
        >
          {showUploadForm ? 'CANCEL' : '+ UPLOAD FILE'}
        </button>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {/* Upload Form */}
      {showUploadForm && (
        <form onSubmit={handleUpload} style={styles.uploadForm}>
          <div style={styles.formRow}>
            <input
              type="file"
              name="file"
              required
              accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
              style={styles.fileInput}
            />
          </div>
          <div style={styles.formRow}>
            <select name="category_id" style={styles.select}>
              <option value="">No Category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div style={styles.formRow}>
            <input
              type="text"
              name="description"
              placeholder="Description (optional)"
              style={styles.input}
            />
          </div>
          <div style={styles.formRow}>
            <input
              type="text"
              name="tags"
              placeholder="Tags (comma separated)"
              style={styles.input}
            />
          </div>
          <button
            type="submit"
            disabled={uploading}
            style={{...styles.submitButton, opacity: uploading ? 0.6 : 1}}
          >
            {uploading ? 'UPLOADING...' : 'UPLOAD'}
          </button>
        </form>
      )}

      {/* Search & Filter */}
      <div className="module-filterbar" style={styles.filterBar}>
        <input
          type="text"
          placeholder="Search files..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
        <select
          value={selectedCategory || ''}
          onChange={(e) => setSelectedCategory(e.target.value || null)}
          style={styles.categorySelect}
        >
          <option value="">All Categories</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>
              {cat.name} ({cat.file_count})
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      <div className="module-content" style={styles.content}>
        {/* File List */}
        <div style={styles.fileList}>
          {loading ? (
            <div style={styles.loading}>Loading...</div>
          ) : files.length === 0 ? (
            <div style={styles.empty}>
              <div style={styles.emptyIcon}>📁</div>
              <div style={styles.emptyText}>No files in vault</div>
              <div style={styles.emptyHint}>Upload your first file to get started</div>
            </div>
          ) : (
            files.map(file => (
              <div
                key={file.id}
                style={{
                  ...styles.fileItem,
                  background: selectedFile?.id === file.id ? 'rgba(255, 215, 0, 0.1)' : 'transparent'
                }}
                onClick={() => setSelectedFile(file)}
              >
                <div style={styles.fileIcon}>📄</div>
                <div style={styles.fileInfo}>
                  <div style={styles.fileName}>{file.original_name}</div>
                  <div style={styles.fileMeta}>
                    {(file.file_size / 1024).toFixed(1)} KB • {new Date(file.uploaded_at).toLocaleDateString()}
                  </div>
                  {file.tags.length > 0 && (
                    <div style={styles.fileTags}>
                      {file.tags.map(tag => (
                        <span key={tag} style={styles.tag}>{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* File Detail Panel */}
        {selectedFile && (
          <div style={styles.detailPanel}>
            <div style={styles.detailHeader}>FILE DETAILS</div>
            <div style={styles.detailBody}>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Filename:</span>
                <span>{selectedFile.original_name}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Size:</span>
                <span>{(selectedFile.file_size / 1024).toFixed(2)} KB</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Type:</span>
                <span>{selectedFile.mime_type}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Uploaded:</span>
                <span>{new Date(selectedFile.uploaded_at).toLocaleString()}</span>
              </div>
              {selectedFile.description && (
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Description:</span>
                  <span>{selectedFile.description}</span>
                </div>
              )}
              <div style={styles.detailActions}>
                <button
                  style={styles.downloadButton}
                  onClick={() => handleDownload(selectedFile.id, selectedFile.original_name)}
                >
                  DOWNLOAD
                </button>
                {user?.role === 'SUPER_ADMIN' && (
                  <button
                    style={styles.deleteButton}
                    onClick={() => handleDelete(selectedFile.id)}
                  >
                    DELETE
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '40px',
    color: '#FFD700',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    opacity: 0.7,
  },
  uploadButton: {
    padding: '12px 24px',
    background: '#FFD700',
    color: 'black',
    border: 'none',
    borderRadius: '4px',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: '14px',
  },
  uploadForm: {
    background: 'rgba(255, 215, 0, 0.1)',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '20px',
    border: '1px solid #FFD700',
  },
  formRow: {
    marginBottom: '15px',
  },
  fileInput: {
    width: '100%',
    padding: '10px',
    background: 'black',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
  },
  input: {
    width: '100%',
    padding: '10px',
    background: 'black',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '10px',
    background: 'black',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    outline: 'none',
  },
  submitButton: {
    padding: '12px 24px',
    background: '#FFD700',
    color: 'black',
    border: 'none',
    borderRadius: '4px',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: '14px',
    width: '100%',
  },
  filterBar: {
    display: 'flex',
    gap: '15px',
    marginBottom: '20px',
  },
  searchInput: {
    flex: 1,
    padding: '10px',
    background: 'black',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    outline: 'none',
  },
  categorySelect: {
    width: '250px',
    padding: '10px',
    background: 'black',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    outline: 'none',
  },
  content: {
    flex: 1,
    display: 'flex',
    gap: '20px',
    overflow: 'hidden',
  },
  fileList: {
    flex: 1,
    overflowY: 'auto',
    border: '1px solid #FFD700',
    borderRadius: '8px',
    padding: '10px',
  },
  fileItem: {
    display: 'flex',
    gap: '15px',
    padding: '15px',
    borderBottom: '1px solid rgba(255, 215, 0, 0.2)',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  fileIcon: {
    fontSize: '32px',
  },
  fileInfo: {
    flex: 1,
  },
  fileName: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '5px',
  },
  fileMeta: {
    fontSize: '12px',
    opacity: 0.7,
    marginBottom: '5px',
  },
  fileTags: {
    display: 'flex',
    gap: '5px',
    flexWrap: 'wrap',
  },
  tag: {
    fontSize: '11px',
    padding: '2px 8px',
    background: 'rgba(255, 215, 0, 0.2)',
    borderRadius: '4px',
  },
  detailPanel: {
    width: '350px',
    border: '1px solid #FFD700',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  detailHeader: {
    padding: '15px',
    background: 'rgba(255, 215, 0, 0.1)',
    fontWeight: 'bold',
    fontSize: '14px',
    borderBottom: '1px solid #FFD700',
  },
  detailBody: {
    padding: '20px',
  },
  detailRow: {
    marginBottom: '15px',
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  detailLabel: {
    fontSize: '12px',
    opacity: 0.7,
    textTransform: 'uppercase',
  },
  detailActions: {
    marginTop: '30px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  downloadButton: {
    padding: '12px',
    background: '#FFD700',
    color: 'black',
    border: 'none',
    borderRadius: '4px',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: '14px',
  },
  deleteButton: {
    padding: '12px',
    background: 'transparent',
    color: '#ff4444',
    border: '1px solid #ff4444',
    borderRadius: '4px',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: '14px',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '16px',
    opacity: 0.7,
  },
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '20px',
  },
  emptyText: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  emptyHint: {
    fontSize: '14px',
    opacity: 0.7,
  },
  error: {
    padding: '15px',
    background: 'rgba(255, 0, 0, 0.1)',
    border: '1px solid #ff4444',
    borderRadius: '8px',
    color: '#ff4444',
    marginBottom: '20px',
  },
  accessDenied: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    padding: '40px',
  },
  accessTitle: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#ff4444',
    marginBottom: '20px',
  },
  accessMessage: {
    fontSize: '16px',
    opacity: 0.7,
    marginBottom: '10px',
  },
  accessRole: {
    fontSize: '14px',
    opacity: 0.5,
  },
};
